import { useEffect } from 'react';
import axios from 'axios';
import ExcelDownload from './ExcelDownload';
import useModalStore from '../../../zustand/store/useModalStore';

import * as Icons from '../../../images/icons/icons_component'
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';

/**
 * @공통SearchBtn 해당 컴포넌트로 넘기는 props
    <SearchBtn 
        propsInfo={propsInfo}
        state={state}
        setState={setState}
        searchParams={searchParams}
        searchParamsAdditional={searchParamsAdditional}
        pageInfo={pageInfo}
        setPageInfo={setPageInfo}
    />
 * @propsInfo props로 넘길 정보 묶음 - SearchBtn
    dataHead: 그리드에 보여질 헤더 이름
    dataColumn: 헤더 이름과 매칭되는 데이터 컬럼명
    apiCall: 검색 URL
    apiCntCall: 데이터 카운터 URL (사용하지 않으면 빈 문자열)
    excelFileName: 엑셀 파일 이름 (엑셀 다운로드가 필요없는 화면은 빈 문자열)
    excelHead: 엑셀 다운로드 시 그리드 헤더가 보여지는 화면과 다를 때 추가 작성 (dataHead와 같으면 빈 배열)
  <sample>
   -- 필요한 정보를 각각 변수를 선언해서 담아준다
    const dataHead = ['번호', '내원일자', '시간', '등록번호', '이름', '나이/성별', '상태', '전송일자'];
    const dataColumn = ['SEQ', 'PTMIINDT', 'PTMIINTM', 'PTMIIDNO', 'PTMINAME', 'PTMISEXX', 'PTMISTAT', 'PTMIWKDT'];
    const apiCall = '/jinryo/selectEMIHPTMIList.do';
    const apiCntCall = '/jinryo/selectEMIHPTMIListCnt.do';
    const excelFileName = '대상환자조회';
    const excelHead = [];
   -- 변수를 propsInfo에 담아 props로 넘겨준다
    const propsInfo = {
        dataHead: dataHead, 
        dataColumn: dataColumn, 
        apiCall: apiCall, 
        apiCntCall: apiCntCall,
        excelFileName: excelFileName,
        excelHead: excelHead
    }
 * @state @setState 기본적으로 필요한 useState 기본 세팅 값(dataList, isSearched, isLoading)
  <sample>
   -- 세팅값은 기본이니 그대로 작성하여 props로 state, setState를 넘겨준다
    const [state, setState] = useState({
        dataList: [],
        isSearched: false,
        isLoading: false
    })
 * @searchParams 넘겨줄 (VO에 정의된) 기본 parameter
  <sample>
   -- 공통으로 사용하는 parameter(기관구분, 지역, 기관명, 시작일자, 종료일자 등)는 변수 그대로 사용하기
   -- @validationCheckList 유효성 체크가 필요한 항목의 변수를 대소문자 정확히 '순서대로' 넘겨주기(유효성 체크가 필요없으면 아예 넘기지 않거나 빈 배열을 넘겨준다.)
      !!! 날짜 변수가 searchParams에 들어가 있으면 날짜 유효성 체크는 기본 값이기 때문에 따로 넘겨줄 필요없음.
      !!! 넘겨준 유효성 체크 항목이 keyLabelMap에 없으면 변수 그대로 넘어가니 없을 경우에는 추가 필수
    const [searchParams, setSearchParams] = useState({
        searchFromdate: new Date((new Date()).getTime() + 1000 * 60 * 60 * 9).toISOString().split('T')[0],
        searchTodate: new Date((new Date()).getTime() + 1000 * 60 * 60 * 9).toISOString().split('T')[0],
        searchHosp: '',
        searchGubun: '',
        searchDateCls: '01',
        searchPtmiIdno: '',
        searchEmog: '',
        searchLoca: '',
        validationCheckList: ['searchHosp','searchPtmiIdno']
    });
 * @searchParamsAdditional 공통 parameter 외에 추가로 넘겨줄 parameter (없으면 빈 객체)
  <sample>
    const searchParamsAdditional = {
        procBtn: 'List3'
    };
 * @pageInfo @setPageInfo 페이지네이션 사용 여부, 전체 데이터 수
  <sample>
   -- pagination 사용 여부에 따라  paginationTF를 boolean 값으로 적기
   -- pagination을 사용하지 않아도 currentPage, itemsPerPage는 그대로 넘겨주기
    const [pageInfo, setPageInfo] = useState({
        paginationTF: false,
        currentPage: 1,
        itemsPerPage: 50, // 한 페이지당 보여줄 데이터 수
        totalItems: 0 // 전체 데이터 수
    });
 * @reSearch @setReSearch 데이터 등록, 수정, 삭제 시 변경된 데이터를 다시 search 해야할 때(사용하지 않을 시 안 넘겨줘도 됨)
    const [reSearch, setReSearch] = useState(false);
 * @children 검색 버튼 오른쪽에 같은 줄로 표시할 버튼(추가, 등록 등). 사용하지 않을 시 생략
    <SearchBtn ...>
        <button className={`search_btn ${primaryBackColor}`} onClick={...}><Icons.AddCircle16w />추가</button>
    </SearchBtn>
 * @lockOwnOrg J003 사용자의 센터구분 · 지역 · 기관을 본인 기관으로 고정할지(기본 true).
    본인 기관 밖을 찾아야 하는 화면(사용자관리 기관검색)만 false 로 넘긴다. NewCommonSelect 에도 같은 값을 넘긴다
 */

function SearchBtn({ propsInfo, state, setState, searchParams, searchParamsAdditional, pageInfo, setPageInfo, mergeConfig, reSearch, setReSearch, children, lockOwnOrg = true }) {

    const dataHead = propsInfo.dataHead;
    const dataColumn = propsInfo.dataColumn;
    const apiCall = propsInfo.apiCall;
    const apiCntCall = propsInfo.apiCntCall;
    const excelFileName = propsInfo.excelFileName;
    const excelHead = propsInfo.excelHead.length === 0 ? dataHead : propsInfo.excelHead;

    const validationCheckList = searchParams.validationCheckList;

    const dataList = state.dataList;
    const isSearched = state.isSearched;
    const isLoading = state.isLoading;

    const paginationTF = pageInfo.paginationTF;
    const perpage = pageInfo.itemsPerPage;

    if (searchParamsAdditional != null) {
        searchParamsAdditional.pageNum = parseInt(pageInfo.currentPage);
        searchParamsAdditional.displayRowsize = parseInt(pageInfo.itemsPerPage);
    }

    if (lockOwnOrg && localStorage.getItem("userAuth") === "J003") {
        searchParams.searchGrdc = localStorage.getItem("emoggrdc2");
        searchParams.searchLoca = localStorage.getItem("emogloca");
        searchParams.searchEmog = localStorage.getItem("useremcd");
    }

    const totalObj = Object.assign({}, searchParams, searchParamsAdditional);

    // toast modal open function
    const showNotiModal = useModalStore(state => state.showNotiModal);

    const fetchData = async () => {
        try {
            delete totalObj.validationCheckList;
            const response = await axios.get(apiCall,
                { params: totalObj }
            );
            setState(prev => ({
                ...prev,
                dataList: response.data,
                isSearched: true
            }));
            if (apiCntCall === '') {
                setPageInfo(prev => ({
                    ...prev,
                    totalItems: response.data.length
                }));
            } else {
                fetchDataCnt();
            }
            setState(prev => ({
                ...prev,
                isLoading: false
            }));
        } catch (error) {
            console.error('데이터 로딩 중 오류 발생:', error);
        }
    };

    const fetchDataCnt = async () => {
        try {
            const response = await axios.get(apiCntCall,
                { params: totalObj }
            );
            setPageInfo(prev => ({
                ...prev,
                totalItems: response.data
            }));
        } catch (error) {
            console.error('데이터 카운터 중 오류 발생:', error);
        }
    };

    const getDateDiff = (date1, date2) => {

        const parseDate = (str) => {
            const year = parseInt(str.substring(0, 4));
            const month = parseInt(str.substring(4, 6)) - 1;
            const day = parseInt(str.substring(6, 8));
            return new Date(year, month, day);
        };

        const d1 = parseDate(date1);
        const d2 = parseDate(date2);

        // 차이 계산 (밀리초 → 일 단위)
        const diffTime = d2 - d1;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        return diffDays;
    };

    // 재조회 트리거가 둘이라 effect 도 둘로 나눈다.
    //
    // 하나로 합쳐 두면 조회 1번에 서버 호출이 3번 나간다.
    // 관련 없는 두 트리거가 같은 의존성 배열을 공유하기 때문이다.
    //   ① reSearch 가 true 로 올라옴  → 두 조건이 모두 성립해 fetchData 2번
    //   ② setReSearch(false) 로 내려감 → 배열이 또 바뀌어 fetchData 1번
    // 서버 부하도 3배이고, 접속로그에도 같은 조회가 3행씩 남는다.

    // 페이지 이동 · 구분 변경 시 재조회 (검색을 한 뒤에만)
    // search() 가 isSearched 를 false 로 내린 뒤 currentPage 를 1로 바꾸므로,
    // 조회 버튼을 눌렀을 때 이 effect 가 중복으로 돌지 않는다.
    useEffect(() => {
        if (state.isSearched) {
            setState(prev => ({
                ...prev,
                isLoading: true
            }));
            fetchData();
        }
    }, [pageInfo.currentPage, searchParams.searchGubunS]);

    // 등록 · 수정 · 삭제 후 재조회.
    // 플래그를 내리면서 이 effect 가 한 번 더 돌지만 reSearch 가 거짓이라 아무것도 하지 않는다.
    useEffect(() => {
        if (reSearch) {
            setState(prev => ({
                ...prev,
                isLoading: true
            }));
            fetchData();
            setReSearch(false);
        }
    }, [reSearch]);

    const search = () => {
        const keyLabelMap = {
            searchEmog: '먼저 해당 응급의료기관을 선택해주세요!',
            searchLoca: '지역을 선택해주세요.',
            searchIdno: '등록번호 값은 필수입니다.',
        };
        const searchParamsKeys = Object.keys(searchParams);
        const hasDateValidation = searchParamsKeys?.includes('searchFromdate') && searchParamsKeys?.includes('searchTodate');
        
        if (hasDateValidation) {
            const today = new Date();
            const year = today.getFullYear();
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const day = today.getDate().toString().padStart(2, '0');
            const todayDate = `${year}${month}${day}`;
            const fromDate = searchParams.searchFromdate.replace(/-/g, '');
            const toDate = searchParams.searchTodate.replace(/-/g, '');
            const diff = getDateDiff(fromDate, toDate);
            
            if (todayDate < fromDate) {
                showNotiModal('검색 시작일자가 현재 시간보다 미래일 수 없습니다.');
                return;
            }
            if (todayDate < toDate) {
                showNotiModal('검색 종료일자가 현재 시간보다 미래일 수 없습니다.');
                return;
            }
            if (Number(toDate) < Number(fromDate)) {
                showNotiModal('검색 종료일자가 검색 시작일자보다 과거일 수 없습니다.');
                return;
            }
            if ((validationCheckList?.[0] === 'searchLoca') && diff > 6) {
                showNotiModal('전체기관 검색기간은 일주일을 초과할 수 없습니다.');
                return;
            }
            if (!(fromDate !== '' && toDate !== '')) {
                showNotiModal('검색기간을 선택해주세요.');
                return;
            }
            if (diff > 1095) {
                showNotiModal('검색 기간이 3년을 초과할 수 없습니다.');
                return;
            }
        };

        // 일반 유효성 검사
        const isValid = validationCheckList?.every(key => {
            if (!searchParams[key]) {
                const label = keyLabelMap[key] || key;
                showNotiModal(label);
                return false;
            }
            return true;
        });

        if (isValid === false) return;

        setState(prev => ({
            ...prev,
            isSearched: false,
            isLoading: true
        }));
        setPageInfo(prev => ({
            ...prev,
            currentPage: 1,
        }));
        fetchData();
    };

    // 페이지네이션 페이지 이동
    const onHandlePerPage = (e) => {
        setPageInfo(prev => ({
            ...prev,
            itemsPerPage: parseInt(e.target.value)
        }));
    };

    const perPageOptions = Array.from({ length: 10 }, (_, i) => {
        const perPage = (i + 1) * 10;
        return (
            <option key={perPage} value={perPage}>
                -- {perPage}건 씩 조회 --
            </option>
        );
    });

    // Dynamic Style Hook
    const { fontColor, primaryBackColor } = useDynamicStyle();

    return (
        <div className='search_btn_wrapper'>
            <span className='total_items'>전체&nbsp;<strong className={fontColor}>{pageInfo.totalItems}</strong>건</span>
            <div className='btn_container'>
                {!paginationTF ? <></> :
                    <select
                        name="itemsPerPage"
                        className='page_select'
                        value={perpage}
                        onChange={onHandlePerPage}
                    >
                        {perPageOptions}
                    </select>}
                <button onClick={search} disabled={isLoading ? 'disabled' : ''} className={`search_btn ${primaryBackColor}`}><Icons.Search16w />검색</button>
                {children}
                {excelFileName === '' ? <div></div> :
                    <ExcelDownload
                        excelHead={excelHead}
                        excelData={dataList}
                        searched={isSearched}
                        dataCode={dataColumn}
                        excelFileName={excelFileName}
                        mergeData={mergeConfig}
                    />
                }
            </div>
        </div>
    )
}

export default SearchBtn